import { ECashierFlows } from "@/utils/enums";

export const DEFAULT_ID = "default";

export type ENVIRONMENT = "development" | "production" | "staging";

export const MOBILE_BREAKPOINT = 768;

export enum GLOBAL_NAVIGATION_URLS {
  DASHBOARD = "dashboard",
  TRADING_PLATFORMS = "trading-platforms",
  TRADING_ACCOUNTS = "trading-accounts",
  SCA_BASE = "secure-client-area",
  CREATE_TRADING_ACCOUNT = "create-trading-account",
}

export const REQUIRED_PAYMENT_METHOD_FIELDS = ["email"];

const DEFAULT_BASE_DOMAIN = "pepperstone.com";

// Secure Client Area (SCA) subdomain per build environment. The SCA host is
// always `<subdomain>.<baseDomain>`.
const SCA_SUBDOMAIN_BY_ENV: Record<ENVIRONMENT, string> = {
  development: "secure-dev",
  staging: "secure-staging",
  production: "secure",
};

// Pretrade dashboard host per env. Shared marketing infra, NOT provisioned
// per-brand (there is no next.pretrade.<brand> DNS), so it always stays on
// pepperstone.com regardless of the brand the MFE is embedded in.
const DASHBOARD_URL_BY_ENV: Record<ENVIRONMENT, string> = {
  development: "https://next.dev.pretrade.pepperstone.com/",
  staging: "https://next.stg.pretrade.pepperstone.com/",
  production: "https://next.pretrade.pepperstone.com/",
};

const getEnv = (): ENVIRONMENT => {
  const modeToEnv: Record<string, ENVIRONMENT> = {
    development: "development",
    "staging-standalone": "staging",
    staging: "staging",
    "migration-standalone": "production",
    "production-standalone": "production",
    production: "production",
  };
  // fallback to DEV (localhost, ephemeral, test)
  return modeToEnv[import.meta.env.MODE] ?? "development";
};

export { getEnv };

/**
 * Registrable base domain of the host the Cashier MFE is embedded in, e.g.
 * "mypepperfx.com" for secure.mypepperfx.com. Lets SCA redirects stay within the
 * visitor's own white-label / GSR brand instead of bouncing to pepperstone.com.
 * Falls back to pepperstone.com for localhost / IPs / single-label hosts.
 *
 * Assumes two-label brand domains (`<brand>.<tld>`) — true for every brand in
 * infrastructure/backend/prd/terraform.tfvars -> cors_origins_for.cashier.
 */
const getBaseDomain = (): string => {
  const labels = window.location.hostname.split(".");
  const tld = labels.at(-1) ?? "";
  if (labels.length < 2 || /^\d+$/.test(tld)) {
    return DEFAULT_BASE_DOMAIN;
  }
  return labels.slice(-2).join(".");
};

export const getGlobalNavigationUrl = (key: GLOBAL_NAVIGATION_URLS): string => {
  const env = getEnv();

  if (key === GLOBAL_NAVIGATION_URLS.DASHBOARD) {
    return DASHBOARD_URL_BY_ENV[env];
  }

  const scaBaseUrl = `https://${SCA_SUBDOMAIN_BY_ENV[env]}.${getBaseDomain()}`;
  switch (key) {
    case GLOBAL_NAVIGATION_URLS.TRADING_ACCOUNTS:
      return `${scaBaseUrl}/accounts`;
    case GLOBAL_NAVIGATION_URLS.CREATE_TRADING_ACCOUNT:
      return `${scaBaseUrl}/accounts?modals%5B0%5D=CreateAccountModal`;
    case GLOBAL_NAVIGATION_URLS.TRADING_PLATFORMS:
      return `${scaBaseUrl}/downloads`;
    default:
      return scaBaseUrl;
  }
};

/**
 * Global navigation to go to a specific Pepperstone site
 *
 * @param key - the key of the url to go to
 * @param replace - whether to replace the current history entry or open in a new tab
 *
 * @usage globalNavigation(GLOBAL_NAVIGATION_URLS.TRADING_PLATFORMS); // opens a new tab with the Trading Platforms page
 * @usage globalNavigation(GLOBAL_NAVIGATION_URLS.SCA_CASHIER, true); // navigate in place
 */
export const globalNavigation = (
  key: GLOBAL_NAVIGATION_URLS,
  replace?: boolean
) => {
  const url = new URL(getGlobalNavigationUrl(key));
  url.searchParams.set("acquisitionSource", "ps-web-pnx");
  if (!replace) {
    window.open(url, "_blank");
  } else {
    window.location.replace(url);
  }
};

/**
 * Get the legacy cashier URL based on the current route.
 */
export const getLegacyCashierUrl = (
  params: { locale?: string } & Record<string, string>
) => {
  const route = window.location.hash.split("/");
  let path: string;
  switch (route[1]) {
    case ECashierFlows.Deposit:
      path = "/wallet/deposit/payment-method";
      break;
    case ECashierFlows.Withdraw:
      path = "/wallet/withdrawal";
      break;
    case ECashierFlows.Transfer:
      path = "/wallet/transfer";
      break;
    case ECashierFlows.History:
      path = "/wallet/history";
      break;
    default:
      path = "/wallet";
      break;
  }
  const url = new URL(
    path,
    getGlobalNavigationUrl(GLOBAL_NAVIGATION_URLS.SCA_BASE)
  );

  if (route[2]) {
    url.searchParams.set("login", route[2]);
  }
  url.searchParams.set("acquisitionSource", "ps-web-pnx");
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      value && url.searchParams.set(key, value as string);
    });
  }

  return url.href;
};
